import {computed, effect, inject, Injectable, signal, Signal, WritableSignal} from "@angular/core";
import {NotificationService} from "../notification/notification.service";
import {ConfigurationModel, ParamConfig} from "../configuration/configuration.model";
import {TableLookupMethod} from "../configuration/app-entity.model";
import {LoadingStatus} from './loading-status';
import {AnalyticsService, LandingPage} from '../logging/analytics.service';
import {AppUsageNotificationService} from '../logging/app-usage-notification.service';
import {AppSessionService} from "./app-session.service";
import {OrderingContext, OrderingContextIntention} from '../ordering-context/ordering-context';
import {OrderingContextModel} from '../ordering-context/ordering-context.model';
import {OrderType} from '../order/order';
import {NavigationService} from '../navigation/navigation.service';
import {onEach} from '../utils/observable-utils';
import {DeferredInjectable} from '../deferred-injection.service';
import {Subject} from 'rxjs';

export enum OrderingPhase {
    ORDERING,
    PAYMENT,
    COMPLETE
}

export class SessionReset {
    constructor(public retainStore: boolean = false,
                public retainTable: boolean = false,
                public retainOrderType: boolean = false) {
    }
}

@Injectable({providedIn: "root"})
export class SessionModel implements DeferredInjectable {
    private navigationService: NavigationService = inject(NavigationService);
    private notificationService: NotificationService = inject(NotificationService);
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private analyticsService: AnalyticsService = inject(AnalyticsService);
    private appSessionService: AppSessionService = inject(AppSessionService);
    private appUsageNotificationService: AppUsageNotificationService = inject(AppUsageNotificationService);
    private orderingContextModel: OrderingContextModel;

    private static readonly SESSION_STARTED_MINIMUM_DELAY: number = 3500;
    private static readonly ACTIVITY_CHECK_INTERVAL: number = 60 * 1000; // one minute
    private static readonly ACTIVITY_TIMEOUT: number = 4 * 60 * 60 * 1000; // four hours

    public readonly _initialLoadStatus: WritableSignal<LoadingStatus> = signal<LoadingStatus>(LoadingStatus.LOADING);
    public readonly initialLoadStatus: Signal<LoadingStatus> = this._initialLoadStatus.asReadonly();
    public readonly orderingPhase: WritableSignal<OrderingPhase> = signal<OrderingPhase>(undefined);
    private readonly _appSessionId: WritableSignal<string> = signal<string>(undefined);
    public readonly appSessionId: Signal<string> = this._appSessionId.asReadonly();
    private readonly _scanId: WritableSignal<string> = signal<string>(undefined);
    public readonly scanId: Signal<string> = this._scanId.asReadonly();
    private readonly _beaconId: WritableSignal<string> = signal<string>(undefined);
    public readonly beaconId: Signal<string> = this._beaconId.asReadonly();
    private readonly _beaconScanTime: WritableSignal<number> = signal<number>(undefined);
    public readonly beaconScanTime: Signal<number> = this._beaconScanTime.asReadonly();
    private readonly beaconTableNumber: WritableSignal<string> = signal<string>(undefined);

    public readonly effectiveTableLookupMethod: Signal<TableLookupMethod> = computed<TableLookupMethod>(() =>
        this._beaconId() && !this.beaconTableNumber() ? TableLookupMethod.MANUAL
            : this.configurationModel.isWaiterActor() ? TableLookupMethod.MANUAL
                : (this.configurationModel.app()?.tableLookupMethod ?? TableLookupMethod.MANUAL));

    public readonly noTableMessage: Signal<string> = computed(() =>
        this.effectiveTableLookupMethod() === TableLookupMethod.QR_CODE ? "woa.error.order.scanTable" : "woa.error.order.enterTable");

    /**
     * Notifies that the session has been reset.
     * All models should reset their state accordingly.
     */
    public readonly sessionReset$: Subject<SessionReset> = new Subject();

    private testModeAcknowledged: boolean = false;
    private sessionStarted: number = Date.now();
    private orderingStarted: boolean = false;
    private lastActive: number = Date.now();

    constructor() {
        console.log("--- SessionModel");
        console.log("started=" + this.sessionStarted);

        void this.initSession();

        onEach(this.navigationService.navigated$, () => this.lastActive = Date.now());

        effect(() => {
            if (this.orderingPhase() !== undefined) {
                this.orderingStarted = true;
            }
        });

        setInterval(() => {
            // We currently only time-out waiter logins.
            if (this.configurationModel.requireWaiterName() && this.orderingStarted && Date.now() - this.lastActive > SessionModel.ACTIVITY_TIMEOUT) {
                void this.analyticsService.trackActivityTimeout();
                void this.goToEntryPage();
            }
        }, SessionModel.ACTIVITY_CHECK_INTERVAL);
    }

    public inject(): void {
        this.orderingContextModel = inject(OrderingContextModel);
    }

    private async initSession(): Promise<void> {
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        this._appSessionId.set(this.appSessionService.getAppSessionId(paramConfig.scanId));
        if (paramConfig.unknownBeacon) {
            this.notificationService.logInfoToServer(`Unrecognised beacon ID ${paramConfig.beaconId}`);
            setTimeout(() => this.notificationService.displayAlert("woa.message.unrecognisedBeacon.title", "woa.message.unrecognisedBeacon.subTitle"), 1000);
        } else if (paramConfig.beaconLookupFailed) {
            this.notificationService.logInfoToServer(`Lookup failed for beacon ID ${paramConfig.beaconId}`);
            setTimeout(async () => {
                await this.notificationService.displayAlert("woa.message.beaconLookupFailed.title", "woa.message.beaconLookupFailed.subTitle", undefined, false, "woa.button.common.retry");
                window.location.reload();
            }, 1000);
        } else {
            this._scanId.set(paramConfig.scanId);
            this._beaconId.set(paramConfig.beaconId);
            this._beaconScanTime.set(paramConfig.beaconScanTime);
            this.beaconTableNumber.set(paramConfig.tableLookupCode);
        }
    }

    public async checkLiveMode(): Promise<void> {
        if (!this.configurationModel.app()?.appLiveMode && !this.testModeAcknowledged) {
            this.testModeAcknowledged = await this.notificationService.displayAlertWithCheckbox(
                "woa.message.payment.appNotLive.title", "woa.message.payment.appNotLive.subTitle", "woa.message.payment.appNotLive.acknowledge");
        }
    }

    public clear(reason: string): void {
        this.configurationModel.app.set(undefined);
        this.reset(reason);
    }

    private reset(reason: string, retainStore: boolean = false, retainTable: boolean = false, retainOrderType: boolean = false): void {
        console.log("Session reset requested:", reason);
        this.sessionReset$.next(new SessionReset(retainStore, retainTable, retainOrderType));
    }

    public async notifyAppUsage(entry: boolean): Promise<void> {
        if (!this.configurationModel.isConsumerActor() || this.configurationModel.previewToken()) {
            return;
        }

        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();
        return this.appUsageNotificationService.performAction({
            entry: entry,
            appSessionId: this._appSessionId(),
            scanId: this._scanId(),
            beaconId: this._beaconId(),
            appId: this.configurationModel.app()?.id ?? undefined,
            appName: this.configurationModel.app()?.appName ?? undefined,
            liveMode: this.configurationModel.app()?.appLiveMode ?? undefined,
            tableNumber: paramConfig.tableLookupCode,
            beaconOrderType: (paramConfig.tableOnlyMode && paramConfig.pickupOnlyMode) ? "TABLE_OR_PICKUP_ONLY"
                : paramConfig.tableOnlyMode ? "TABLE_ONLY"
                    : paramConfig.pickupOnlyMode ? "PICKUP_ONLY"
                        : undefined,
            storeId: this.orderingContextModel.currentStore() ? this.orderingContextModel.currentStore().storeId
                : paramConfig.exclusiveStore ? paramConfig.exclusiveStore
                    : paramConfig.store ? paramConfig.store
                        : undefined,
            storeGroupId: paramConfig.exclusiveStoreGroup
        });
    }

    public notifyInitialLoadStatus(status: LoadingStatus) {
        setTimeout(() => {
            this._initialLoadStatus.set(status);
            if (status === LoadingStatus.LOADED) {
                document.body.classList.add("initialLoadComplete");
            }
        }, SessionModel.SESSION_STARTED_MINIMUM_DELAY - (Date.now() - this.sessionStarted));
    }

    public async goToEntryPage(suppressReset: boolean = false): Promise<void> {
        return this.navigationService.createPage("woa-entry-page", {suppressReset: suppressReset}, undefined, true);
    }

    public async goToLandingPage(suppressReset: boolean = false, setRoot: boolean = false): Promise<void> {
        if (!suppressReset) {
            this.reset("go to landing page");
        }

        if (this.orderingContextModel.menuId()) {
            await this.analyticsService.trackLandingPage(LandingPage.ORDERING);
            return this.navigationService.createPage("woa-ordering-page", undefined, undefined, true);
        }

        if (!this.orderingContextModel.stores() && !await this.orderingContextModel.loadStoresForSelection(new OrderingContext())) {
            return this.analyticsService.trackLandingPage(LandingPage.ENTRY);
        }

        if (this.orderingContextModel.singleStore() || this.orderingContextModel.currentStore()) {
            await this.analyticsService.trackLandingPage(LandingPage.ORDERING);
            return this.navigationService.createPage("woa-ordering-page", undefined, undefined, true);
        }

        if (setRoot) {
            await this.analyticsService.trackLandingPage(LandingPage.ENTRY);
            return this.goToEntryPage(suppressReset);
        }

        await this.analyticsService.trackLandingPage(LandingPage.STORE_SELECTION);
        this.notifyInitialLoadStatus(LoadingStatus.LOADED);
        await this.orderingContextModel.selectAndApplyStoreAndOrderType(new OrderingContext()
            .withIntention(OrderingContextIntention.SELECT_STORE)
            .withSelectedTable(this.orderingContextModel.preselectedTableSelection()));
        if (this.orderingContextModel.currentStore()) {
            return this.navigationService.createPage("woa-ordering-page", undefined, undefined, true);
        }
    }

    /**
     * For a child app, return to the parent app.
     * For a table order, return to the ordering page to encourage further ordering.
     * Otherwise, return to the normal landing page.
     */
    public async restartAfterOrderCompletion(retainTable?: boolean): Promise<void> {
        if (this.orderingContextModel.parentAppURL()) {
            return this.orderingContextModel.routeToParentApp();
        }

        if (this.orderingContextModel.orderType() === OrderType.ORDER_AT_TABLE) {
            if (!this.orderingContextModel.hasStoreGroup() || retainTable != undefined) {
                this.reset("restart - store retained", true, retainTable == undefined ? true : retainTable, true);
                return this.navigationService.createPage("woa-ordering-page", undefined, undefined, true);
            }

            if (this.configurationModel.app()?.defaultMenuId) {
                this.reset("restart - store not retained, default menu", false, false, true);
                return this.navigationService.createPage("woa-ordering-page", undefined, undefined, true);
            }

            this.reset("restart - store not retained, no default menu", false, false, true);
            return this.goToEntryPage(true);
        }

        return this.goToLandingPage(false, true);
    }
}
